import { getApiErrorMessage } from '@moko/utils';
import { Actions } from '../actions';

const ADMIN_ROLE = 'FMSPortal.AdminAccess';
const VIEWER_ROLE = 'FMSPortal.ViewerAccess';

export type SlaOffsetUnit = 'BusinessDays' | 'Hours';

export interface ITimeServiceRule {
  timeServiceId: number;
  siteDueOffset: number | null;
  siteDueOffsetUnit: SlaOffsetUnit | null;
  resultsDueOffset: number | null;
  resultsDueOffsetUnit: SlaOffsetUnit | null;
  dueTime: string;
  orderCutoffTime: string;
  isActive: boolean;
}

export type SlaRuleUsed = 'global' | 'tenant' | 'none';

export interface EffectiveSla {
  used: SlaRuleUsed;
  rule: ITimeServiceRule | null;
  disabledOverride: boolean;
  siteUnresolved: boolean;
}

export type SlaSiteChoice = 'unconfigured' | 'configured';

export interface SlaRuleFormValues {
  siteChoice: SlaSiteChoice;
  siteDueOffset: string;
  siteDueOffsetUnit: '' | SlaOffsetUnit;
  resultsDueOffset: string;
  resultsDueOffsetUnit: '' | SlaOffsetUnit;
  dueTime: string;
  orderCutoffTime: string;
  isActive: boolean;
}

const MANAGER_ROLE = 'FMSManager.Access';
const OFFSET_MAX = 9999999.99;
const OFFSET_PATTERN = /^(?:0|[1-9]\d*)(?:\.\d{1,2})?$/;
const INTEGER_PATTERN = /^(?:0|[1-9]\d*)$/;
const WALL_TIME = /^([01]\d|2[0-3]):([0-5]\d):([0-5]\d)$/;

const isUnit = (value: unknown): value is SlaOffsetUnit =>
  value === 'BusinessDays' || value === 'Hours';

const readNumber = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

export const asTimeServiceRule = (value: unknown): ITimeServiceRule | null => {
  if (!value || typeof value !== 'object') return null;
  const record = value as Record<string, unknown>;
  const timeServiceId = record.timeServiceId;
  if (typeof timeServiceId !== 'number' || !Number.isInteger(timeServiceId)) {
    return null;
  }
  const siteDueOffset =
    record.siteDueOffset == null ? null : readNumber(record.siteDueOffset);
  const siteDueOffsetUnit =
    record.siteDueOffsetUnit == null ? null : record.siteDueOffsetUnit;
  const siteConfigured = siteDueOffset != null || siteDueOffsetUnit != null;
  if (siteConfigured && (siteDueOffset == null || !isUnit(siteDueOffsetUnit))) {
    return null;
  }
  return {
    timeServiceId,
    siteDueOffset,
    siteDueOffsetUnit: isUnit(siteDueOffsetUnit) ? siteDueOffsetUnit : null,
    resultsDueOffset: readNumber(record.resultsDueOffset),
    resultsDueOffsetUnit: isUnit(record.resultsDueOffsetUnit)
      ? record.resultsDueOffsetUnit
      : null,
    dueTime: typeof record.dueTime === 'string' ? record.dueTime : '',
    orderCutoffTime:
      typeof record.orderCutoffTime === 'string' ? record.orderCutoffTime : '',
    isActive: record.isActive === true,
  };
};

export const asTimeServiceRules = (payload: unknown): ITimeServiceRule[] =>
  Array.isArray(payload)
    ? payload.flatMap((item) => {
        const rule = asTimeServiceRule(item);
        return rule ? [rule] : [];
      })
    : [];

export const rulesByServiceId = (rules: ITimeServiceRule[]) => {
  const map = new Map<number, ITimeServiceRule>();
  rules.forEach((rule) => map.set(rule.timeServiceId, rule));
  return map;
};

const unresolvedSite = (rule: ITimeServiceRule) =>
  rule.siteDueOffset == null && rule.siteDueOffsetUnit == null;

export const effectiveSla = (
  tenant: ITimeServiceRule | undefined,
  globalRule: ITimeServiceRule | undefined
): EffectiveSla => {
  const activeTenant = tenant?.isActive ? tenant : undefined;
  const activeGlobal = globalRule?.isActive ? globalRule : undefined;
  const rule = activeTenant ?? activeGlobal ?? null;
  return {
    used: activeTenant ? 'tenant' : activeGlobal ? 'global' : 'none',
    rule,
    disabledOverride: !!tenant && !tenant.isActive,
    siteUnresolved: !!rule && unresolvedSite(rule),
  };
};

export const dueTimeApplies = (rule: ITimeServiceRule) =>
  rule.resultsDueOffsetUnit === 'BusinessDays' ||
  rule.siteDueOffsetUnit === 'BusinessDays';

export const cutoffApplies = (rule: ITimeServiceRule) =>
  rule.siteDueOffsetUnit === 'BusinessDays';

const formatOffset = (value: number) => {
  const text = String(value);
  return text.includes('.') ? text.replace(/0+$/, '').replace(/\.$/, '') : text;
};

export const formatAllowance = (
  offset: number | null,
  unit: SlaOffsetUnit | null
) => {
  if (offset == null || unit == null) return '';
  const amount = formatOffset(offset);
  if (unit === 'Hours') {
    return offset === 1 ? `${amount} elapsed hour` : `${amount} elapsed hours`;
  }
  return offset === 1 ? `${amount} business day` : `${amount} business days`;
};

export const formatWallTime = (value: string) => {
  const match = WALL_TIME.exec(value);
  if (!match) return '';
  const hours = Number(match[1]);
  const period = hours >= 12 ? 'PM' : 'AM';
  const hour12 = hours % 12 || 12;
  const clock =
    match[3] === '00'
      ? `${hour12}:${match[2]}`
      : `${hour12}:${match[2]}:${match[3]}`;
  return `${clock} ${period}`;
};

export const normalizeWallTime = (value: string) => {
  const trimmed = value.trim();
  return /^\d{2}:\d{2}$/.test(trimmed) ? `${trimmed}:00` : trimmed;
};

export const emptySlaRuleForm = (): SlaRuleFormValues => ({
  siteChoice: 'configured',
  siteDueOffset: '',
  siteDueOffsetUnit: '',
  resultsDueOffset: '',
  resultsDueOffsetUnit: '',
  dueTime: '',
  orderCutoffTime: '',
  isActive: true,
});

export const toSlaRuleForm = (
  rule: ITimeServiceRule | undefined
): SlaRuleFormValues => {
  if (!rule) return emptySlaRuleForm();
  const unresolved = unresolvedSite(rule);
  return {
    siteChoice: unresolved ? 'unconfigured' : 'configured',
    siteDueOffset: rule.siteDueOffset == null ? '' : String(rule.siteDueOffset),
    siteDueOffsetUnit: rule.siteDueOffsetUnit ?? '',
    resultsDueOffset:
      rule.resultsDueOffset == null ? '' : String(rule.resultsDueOffset),
    resultsDueOffsetUnit: rule.resultsDueOffsetUnit ?? '',
    dueTime: rule.dueTime,
    orderCutoffTime: rule.orderCutoffTime,
    isActive: true,
  };
};

export const toStoredSlaRuleForm = (
  rule: ITimeServiceRule
): SlaRuleFormValues => ({
  ...toSlaRuleForm(rule),
  isActive: rule.isActive,
});

const offsetError = (raw: string, unit: string, label: string) => {
  const value = raw.trim();
  if (!value) return `${label} is required.`;
  if (!OFFSET_PATTERN.test(value)) {
    return `${label} must be a nonnegative number with at most two decimal places.`;
  }
  if (Number(value) > OFFSET_MAX) return `${label} must be at most 9999999.99.`;
  if (unit !== 'BusinessDays' && unit !== 'Hours')
    return `${label} unit is required.`;
  if (unit === 'BusinessDays' && !INTEGER_PATTERN.test(value)) {
    return 'Business days must be a whole number.';
  }
  return '';
};

const timeError = (raw: string, label: string) =>
  WALL_TIME.test(normalizeWallTime(raw))
    ? ''
    : `${label} must be a time from 00:00:00 to 23:59:59.`;

export const validateSlaRuleForm = (
  values: SlaRuleFormValues
): Partial<Record<keyof SlaRuleFormValues, string>> => {
  const errors: Partial<Record<keyof SlaRuleFormValues, string>> = {};
  if (values.siteChoice === 'configured') {
    const site = offsetError(
      values.siteDueOffset,
      values.siteDueOffsetUnit,
      'Site allowance'
    );
    if (site) {
      errors[site.includes('unit') ? 'siteDueOffsetUnit' : 'siteDueOffset'] =
        site;
    }
  }
  const results = offsetError(
    values.resultsDueOffset,
    values.resultsDueOffsetUnit,
    'Results allowance'
  );
  if (results) {
    errors[
      results.includes('unit') ? 'resultsDueOffsetUnit' : 'resultsDueOffset'
    ] = results;
  }
  const due = timeError(values.dueTime, 'Due time');
  if (due) errors.dueTime = due;
  const cutoff = timeError(values.orderCutoffTime, 'Order cutoff');
  if (cutoff) errors.orderCutoffTime = cutoff;
  return errors;
};

export const toSlaRuleBody = (
  timeServiceId: number,
  values: SlaRuleFormValues
): ITimeServiceRule => ({
  timeServiceId,
  siteDueOffset:
    values.siteChoice === 'unconfigured' ? null : Number(values.siteDueOffset),
  siteDueOffsetUnit:
    values.siteChoice === 'unconfigured'
      ? null
      : values.siteDueOffsetUnit || null,
  resultsDueOffset: Number(values.resultsDueOffset),
  resultsDueOffsetUnit: values.resultsDueOffsetUnit || null,
  dueTime: normalizeWallTime(values.dueTime),
  orderCutoffTime: normalizeWallTime(values.orderCutoffTime),
  isActive: values.isActive,
});

export const activeGlobalRule = (rule: ITimeServiceRule | undefined) =>
  rule?.isActive ? rule : undefined;

export const fallbackLeavesSlaUnconfigured = (
  globalRule: ITimeServiceRule | undefined
) => !activeGlobalRule(globalRule);

export const fallbackSiteUnresolved = (
  globalRule: ITimeServiceRule | undefined
) => {
  const active = activeGlobalRule(globalRule);
  return !!active && unresolvedSite(active);
};

export const defaultTimezoneId = (
  timezones:
    | { timezoneId: string; isDefault: boolean; isDeleted: boolean }[]
    | undefined
) =>
  timezones?.find(
    (item) => item.isDefault && !item.isDeleted && item.timezoneId.trim()
  )?.timezoneId ?? '';

export const canConfigureSlaRules = (
  roles: readonly string[],
  permissions: readonly number[]
) => {
  const admin = roles.includes(ADMIN_ROLE);
  const portal = admin || roles.includes(VIEWER_ROLE);
  const allowed = new Set(permissions);
  return (
    admin &&
    allowed.has(Actions.ManageTenants) &&
    (admin || roles.includes(MANAGER_ROLE)) &&
    allowed.has(Actions.ManageWorks) &&
    portal &&
    allowed.has(Actions.ManageOrders)
  );
};

export const apiStatus = (error: unknown) => {
  const message =
    error instanceof Error
      ? error.message
      : typeof error === 'object' &&
        error !== null &&
        'message' in error &&
        typeof error.message === 'string'
      ? error.message
      : '';
  if (message.includes("don't have permission")) return 403;
  const match = /API Error: (\d{3})/.exec(message);
  return match ? Number(match[1]) : undefined;
};

export const slaFailureMessage = (
  sources: { error: unknown; fallback: string }[]
) => {
  const source = sources.find((item) => item.error != null);
  if (!source) return null;
  return apiStatus(source.error) === 403
    ? "You don't have permission to configure SLA rules."
    : getApiErrorMessage(source.error, source.fallback);
};
